import type { UpdateDeviceTokenRequest } from '@server/domains/users/dto/device-token.schema'
import { rpc } from '@server/infrastructure/rpc'

import type { DeviceTokenUpdateInput } from './native.action'

export type DeviceTokenSyncResult = {
  kind:
    | 'success'
    | 'busy'
    | 'superseded'
    | 'unsupported'
    | 'unauthorized'
    | 'invalid'
    | 'unavailable'
    | 'cancelled'
    | 'unknown'
  status?: number
}

type DeviceSyncResponse = { ok: boolean; status: number; json: () => Promise<unknown> }

const classifyResponse = async (response: DeviceSyncResponse): Promise<DeviceTokenSyncResult> => {
  if (response.ok) {
    return { kind: 'success' }
  }
  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized', status: response.status }
  }
  if (response.status === 400) {
    return { kind: 'invalid', status: response.status }
  }
  if (response.status === 404) {
    return { kind: 'unsupported', status: response.status }
  }
  let code: unknown
  try {
    const body = await response.json()
    if (typeof body === 'object' && body !== null && 'error' in body) {
      const error = body.error
      if (typeof error === 'object' && error !== null && 'code' in error) {
        code = error.code
      }
    }
  } catch {
    // Neither arbitrary response bodies nor parsing errors belong in client logs.
  }
  if (response.status === 409 && code === 'DEVICE_OWNERSHIP_BUSY') {
    return { kind: 'busy', status: 409 }
  }
  if (response.status === 409 && code === 'DEVICE_OWNERSHIP_SUPERSEDED') {
    return { kind: 'superseded', status: 409 }
  }
  if (response.status === 503 && code === 'DEVICE_OWNERSHIP_UNAVAILABLE') {
    return { kind: 'unavailable', status: 503 }
  }
  return { kind: 'unknown', status: response.status }
}

const waitForReconciliation = (delayMs: number, signal?: AbortSignal): Promise<boolean> =>
  new Promise((resolve) => {
    if (signal?.aborted) {
      return resolve(false)
    }
    const finish = (ready: boolean) => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', cancel)
      resolve(ready)
    }
    const cancel = () => finish(false)
    const timer = setTimeout(() => finish(true), delayMs)
    signal?.addEventListener('abort', cancel, { once: true })
  })

/** Write once. Recovery may only confirm state through the separate read-only route. */
export async function synchronizeDeviceTokenFromBrowser(
  input: string | null | DeviceTokenUpdateInput | undefined,
  bearerToken?: string,
  signal?: AbortSignal,
): Promise<DeviceTokenSyncResult> {
  if (signal?.aborted) {
    return { kind: 'cancelled' }
  }
  if (!bearerToken) {
    return { kind: 'unauthorized' }
  }

  try {
    const metadata = typeof input === 'object' && input !== null ? input : undefined
    const deviceToken = metadata ? (metadata.deviceToken ?? null) : (input as string | null)
    const os = metadata?.os
    const normalizedOS =
      os === 'IOS' || os === 'IPAD' || os === 'ios'
        ? 'ios'
        : os === 'ANDROID' || os === 'ANDROID_TABLET' || os === 'android'
          ? 'android'
          : undefined
    let request: UpdateDeviceTokenRequest | { legacyTokenFallback: true }
    if (
      normalizedOS &&
      metadata?.deviceIdentifier &&
      metadata.permissionStatus !== null &&
      metadata.permissionStatus !== undefined &&
      globalThis.crypto?.subtle
    ) {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(metadata.deviceIdentifier))
      const deviceIdentifierHash = Array.from(new Uint8Array(digest), (byte) =>
        byte.toString(16).padStart(2, '0'),
      ).join('')
      request = { deviceToken, deviceIdentifierHash, os: normalizedOS, permissionStatus: metadata.permissionStatus }
    } else {
      // Older WebViews without WebCrypto can still register an available push token.
      // Never send a raw identifier or pretend a tokenless metadata sync succeeded.
      if (!deviceToken && metadata) {
        return { kind: 'invalid' }
      }
      request = deviceToken ? { deviceToken } : { legacyTokenFallback: true }
    }
    if (signal?.aborted) {
      return { kind: 'cancelled' }
    }
    const options = {
      headers: { Authorization: `Bearer ${bearerToken}` },
      // Cancellation only gates work before POST and ignores stale completion.
      // Once sent, await the server response: aborting this fetch would release
      // the provider's FIFO while the old ownership update can still commit.
      init: { credentials: 'same-origin' as const },
      // Background sync must not invoke auth-recovery navigation on older WebViews.
      // A 401 remains false; a later authenticated provider update can resync.
      fetch: (request: RequestInfo | URL, init?: RequestInit) => globalThis.fetch(request, init),
    }
    const response = await rpc.api.v1.users['device-token'].$post({ json: request }, options)
    if (signal?.aborted) {
      return { kind: 'cancelled' }
    }
    let result = await classifyResponse(response)
    // This deadline bounds additional scheduling, never the lifetime of a sent
    // HTTP request. In particular, do not release the provider FIFO on timeout.
    const deadline = Date.now() + 3000
    for (const delay of [200, 500, 1000]) {
      if (result.kind !== 'busy') {
        break
      }
      const wait = delay + Math.floor(Math.random() * 50)
      if (Date.now() + wait > deadline) {
        break
      }
      if (!(await waitForReconciliation(wait, signal)) || signal?.aborted) {
        return { kind: 'cancelled' }
      }
      // A distinct URL fails closed on older servers. Never fall back to the
      // mutation endpoint, even when this observation belongs to an old account.
      const reconciliation = await rpc.api.v1.users['device-token'].reconcile.$post({ json: request }, options)
      result = await classifyResponse(reconciliation)
      if (signal?.aborted) {
        return { kind: 'cancelled' }
      }
    }
    if (result.kind === 'busy') {
      console.warn('Device metadata synchronization pending', { status: result.status })
    } else if (result.kind !== 'success' && !signal?.aborted) {
      console.error('Device metadata synchronization failed', { status: result.status })
    }
    return signal?.aborted ? { kind: 'cancelled' } : result
  } catch {
    // Keep failures observable without recording credentials or arbitrary error bodies.
    if (!signal?.aborted) {
      console.error('Device metadata synchronization failed', { reason: 'transport' })
    }
    return { kind: signal?.aborted ? 'cancelled' : 'unknown' }
  }
}

/** Retain the native 2.0.19/legacy boolean contract without replaying the write. */
export async function updateDeviceTokenFromBrowser(
  input: string | null | DeviceTokenUpdateInput | undefined,
  bearerToken?: string,
  signal?: AbortSignal,
): Promise<boolean> {
  return (await synchronizeDeviceTokenFromBrowser(input, bearerToken, signal)).kind === 'success'
}
